
import { create } from 'zustand';
import { apiErrorText } from '@/shared/api';
import { toast } from '@/shared/ui';
import { TERMINAL_TICKET_STATUSES } from '@/shared/config';
import type { Ticket } from '@/entities/ticket';
import {
  ticketsApi,
  type CreateTicketDto,
  type TicketsListParams,
  type TicketStats,
} from '@/shared/api/tickets';
import { DEFAULT_LIST_LIMIT, mergeRefetched, refetchLimit } from './list-refetch';

interface TicketsState {
  tickets: Ticket[];
  currentTicket: Ticket | null;
  isLoading: boolean;
  currentLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  currentError: string | null;
  total: number;
  lastParams: TicketsListParams | undefined;
  fetchedAt: number;
  stats: TicketStats | null;

  fetchTickets: (params?: TicketsListParams, opts?: { maxAge?: number }) => Promise<void>;
  fetchMore: () => Promise<void>;
  fetchStats: () => Promise<void>;
  fetchTicket: (id: string) => Promise<void>;
  createTicket: (dto: CreateTicketDto) => Promise<Ticket | null>;
  takeTicket: (id: string) => Promise<void>;
  clearCurrent: () => void;
  applyTicketUpdate: (patch: Partial<Ticket> & { id: string }) => void;
}

let listRequestSeq = 0;

let detailRequestSeq = 0;

function statusFitsList(params: TicketsListParams | undefined, status: string): boolean {
  const explicit = params?.status;
  if (explicit) return String(explicit).split(',').includes(status);
  if (params?.includeClosed) return true;
  return !TERMINAL_TICKET_STATUSES.includes(status);
}

let listRefetchTimer: ReturnType<typeof setTimeout> | null = null;

function paramsKey(p: TicketsListParams | undefined): string {
  return JSON.stringify(
    Object.entries(p ?? {})
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

export const useTicketsStore = create<TicketsState>((set, get) => ({
  tickets: [],
  currentTicket: null,
  isLoading: false,
  currentLoading: false,
  isLoadingMore: false,
  error: null,
  currentError: null,
  total: 0,
  lastParams: undefined,
  fetchedAt: 0,
  stats: null,

  fetchTickets: async (params, opts) => {
    if (opts?.maxAge != null) {
      const s = get();
      const fresh =
        paramsKey(params) === paramsKey(s.lastParams) &&
        s.tickets.length > 0 &&
        !s.error &&
        !s.isLoading &&
        Date.now() - s.fetchedAt < opts.maxAge;
      if (fresh) return;
    }
    const seq = ++listRequestSeq;
    const sameQuery = paramsKey(params) === paramsKey(get().lastParams);
    const limit = sameQuery ? refetchLimit(params, get().tickets.length) : undefined;
    set({
      isLoading: true,
      isLoadingMore: false,
      error: null,
      lastParams: params,
      ...(sameQuery ? {} : { tickets: [], total: 0 }),
    });
    try {
      const res = await ticketsApi.list(limit ? { ...params, limit } : params);
      if (seq !== listRequestSeq) return;
      const list = (res.data ?? []) as Ticket[];
      const requested = limit ?? params?.limit ?? DEFAULT_LIST_LIMIT;
      set((s) => ({
        tickets: sameQuery ? mergeRefetched(s.tickets, list, requested) : list,
        total: res.pagination?.total ?? list.length,
        fetchedAt: Date.now(),
      }));
    } catch (err) {
      if (seq !== listRequestSeq) return;
      set({ error: apiErrorText(err, 'Ошибка загрузки заявок') });
    } finally {
      if (seq === listRequestSeq) set({ isLoading: false });
    }
  },

  fetchMore: async () => {
    const { tickets, total, lastParams, isLoading, isLoadingMore } = get();
    if (isLoading || isLoadingMore || tickets.length >= total) return;
    const seq = listRequestSeq;
    set({ isLoadingMore: true });
    try {
      const res = await ticketsApi.list({ ...lastParams, offset: tickets.length });
      if (seq !== listRequestSeq) return;
      const more = (res.data ?? []) as Ticket[];
      set((s) => {
        const seen = new Set(s.tickets.map((t) => t.id));
        return {
          tickets: [...s.tickets, ...more.filter((t) => !seen.has(t.id))],
          total: res.pagination?.total ?? s.total,
        };
      });
    } catch {
    } finally {
      if (seq === listRequestSeq) set({ isLoadingMore: false });
    }
  },

  fetchStats: async () => {
    try {
      const res = await ticketsApi.stats();
      set({ stats: res.data ?? null });
    } catch {
      set({ stats: null });
    }
  },

  fetchTicket: async (id) => {
    const seq = ++detailRequestSeq;
    set({ currentLoading: true, currentError: null });
    try {
      const res = await ticketsApi.get(id);
      if (seq !== detailRequestSeq) return;
      set({ currentTicket: res.data as Ticket });
    } catch (err) {
      if (seq !== detailRequestSeq) return;
      set({ currentError: apiErrorText(err, 'Ошибка загрузки заявки') });
    } finally {
      if (seq === detailRequestSeq) set({ currentLoading: false });
    }
  },

  createTicket: async (dto) => {
    set({ isLoading: true, error: null });
    try {
      const res = await ticketsApi.create(dto);
      const ticket = res.data as Ticket;
      set((s) => ({ tickets: [ticket, ...s.tickets] }));
      return ticket;
    } catch (err) {
      set({ error: apiErrorText(err, 'Ошибка создания заявки') });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },

  takeTicket: async (id) => {
    try {
      const res = await ticketsApi.takeTicket(id);
      const ticket = res.data;
      if (ticket) {
        set((s) => ({
          tickets: s.tickets.map((t) => (t.id === id ? { ...t, ...ticket } : t)),
          currentTicket: s.currentTicket?.id === id ? { ...s.currentTicket, ...ticket } : s.currentTicket,
        }));
      }
      const unchanged = res.meta?.statusUnchanged;
      if (unchanged) {
        toast.warning(unchanged.message, { duration: 8000 });
        return;
      }
      const groupName = ticket?.assignedGroup?.name;
      if (res.meta?.groupMoved && groupName) {
        toast.success(`Заявка взята в работу и передана вашей группе: ${groupName}`);
      } else {
        toast.success('Заявка взята в работу');
      }
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось взять заявку в работу'));
    }
  },

  clearCurrent: () => set({ currentTicket: null, currentError: null }),

  applyTicketUpdate: (patch) => {
    const safe: Partial<Ticket> = {};
    const keys = ['status', 'statusLabel', 'updatedAt', 'massIncident', 'muted'] as const;
    for (const k of keys) {
      if (patch[k as keyof typeof patch] !== undefined) {
        (safe as Record<string, unknown>)[k] = patch[k as keyof typeof patch];
      }
    }
    if (Object.keys(safe).length === 0) return;
    const { tickets, lastParams } = get();
    const inList = tickets.some((t) => t.id === patch.id);
    if (typeof safe.status === 'string' && inList !== statusFitsList(lastParams, safe.status)) {
      if (listRefetchTimer) clearTimeout(listRefetchTimer);
      listRefetchTimer = setTimeout(() => {
        listRefetchTimer = null;
        void get().fetchTickets(get().lastParams ?? {});
      }, 400);
    }
    set((s) => {
      if (!s.tickets.some((t) => t.id === patch.id)) return s;
      return { tickets: s.tickets.map((t) => (t.id === patch.id ? { ...t, ...safe } : t)) };
    });
  },
}));
